import { faCaretCircleLeft, faCaretCircleRight } from "@fortawesome/pro-regular-svg-icons";
import { faRotateLeft } from "@fortawesome/pro-solid-svg-icons/faRotateLeft";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, CardContent, Chip, Stack } from "@mui/material";
import { AppImageDimension } from "app/appImages";
import { MprcCard, MprcRequestIndicator, MprcTypography } from "components";
import Carousel from "components/MprcCarousel/Carousel";
import { BannerControlProps, MprcVideoBanner } from "components/MprcVideoBanner";
import { CustomVideoSlide, CustomVideoSlideEvents } from "components/types/lightBox";
import { FeedAttachDto, PublicationPresentation, WordItemDto } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { NIL } from "uuid";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { publishCardVideoSeekEvent, publishCardVideoTimeBackEvent } from "views/knowledge/components/card/CardEvents";
import { BuildLighboxSlide, CardLightBox } from "views/knowledge/components/card/behaviors/common/CardLightbox";
import { SlideImage } from "yet-another-react-lightbox";
import { CardOwnPropsOptions, CardVideoSearch } from "../..";

////////////////////////////////////////////////////////////////////////////////

const VideoSearchBox = ({ attachId, searchText: text }: CardVideoSearch) => {

    const api = useCoreApi();
    const { t } = useTranslation("shared");

    const [loading, setLoading] = useState<boolean>();
    const [matches, setMatches] = useState<WordItemDto[]>();
    const [currentWordClick, setCurrentWordClick] = useState<string | null>(null);
    const [currentWordIndex, setCurrentWordIndex] = useState<number>(0);

    const load = (searchText: string) => {
        setLoading(true);

        api.v1.publication.getMatchSubtitles({ publicationId: NIL, attachId, searchText: decodeURIComponent(searchText) })
            .then(response => {
                setMatches(response.data);

                //carregar e reproduzir AUTOMATICAMENTE vídeo a partir da posição da primeira transcrição encontrada com os termos de busca
                if (response.data.length > 0) {
                    handleClick(response.data[0]);
                }
            })
            .finally(() => setLoading(false));
    };

    const handleClick = (v: WordItemDto) => {

        if (currentWordClick == null || v.word != currentWordClick) {
            setCurrentWordClick(v.word);
            setCurrentWordIndex(0);
        }
        else if (v.word == currentWordClick) {
            if (currentWordIndex < v.indexes.length - 1)
                setCurrentWordIndex(currentWordIndex + 1);
            else
                setCurrentWordIndex(0);
        }

        // Posiciona
        publishCardVideoSeekEvent({ videoId: attachId, seekTime: Math.ceil(v.indexes[currentWordIndex]) });
    };

    const handleTimeback = () => {
        publishCardVideoTimeBackEvent({ videoId: attachId, seekTime: 10 });
    };

    const BuildLabel = (item: WordItemDto) => {
        var str = item.word;
        if (item.indexes.length > 0)
            str += ` (${item.indexes.length}x)`;

        return str;
    };

    ///
    useEffect(() => {
        load(text);
    }, [text]);


    if (!text)
        return null;
    else if (loading)
        return <MprcRequestIndicator />;
    else if (matches?.length == 0)
        return null;

    return (
        <MprcCard paperColor="notice" sx={{ width: "100%", my: 1, mx: 0.5 }}>
            <CardContent sx={{ p: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <div>
                        <MprcTypography variant="overline">{t("GlobalSearch.VideoResultsFoundTitle")}</MprcTypography>
                        <Stack direction="row" spacing={1}>
                            {matches?.map(item => <Chip key={item.word} label={BuildLabel(item)} onClick={() => handleClick(item)} />)}
                        </Stack>
                    </div>
                    <Button
                        startIcon={<FontAwesomeIcon icon={faRotateLeft} size="sm" />}
                        color="muted"
                        onClick={handleTimeback}
                    >
                        10s
                    </Button>
                </Stack>
            </CardContent>
        </MprcCard >
    );
};

////////////////////////////////////////////////////////////////////////////////

interface IImageBannerProps {
    onClick: () => void;
}

const ImageBanner = ({ src, alt, description, title, offset, width, height, onClick }: IImageBannerProps & SlideImage & BannerControlProps) => {
    const isLarge = width && height
        ? (width > AppImageDimension.CardPicture.width || height > AppImageDimension.CardPicture.height)
        : true;

    const isVertical = width && height && width < height;

    return (
        <div
            style={
                {
                    width: isVertical ? "unset" : isLarge ? "100%" : width,
                    height: isVertical ? "unset" : isLarge ? "100%" : height,
                    cursor: "pointer"
                }
            }
            onClick={onClick}
        >
            {
                offset == 0 &&
                <img
                    src={src}
                    alt={alt}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: isVertical ? "contain" : "cover"
                    }}
                    title={description as string}
                />
            }
        </div>
    );
};

////////////////////////////////////////////////////////////////////////////////

type BannerProps = BannerControlProps & IImageBannerProps & {
    item: CustomVideoSlide | SlideImage;
};

const Banner = ({ item, ...rest }: BannerProps) => {
    return (
        <div className="banner"
            style={{
                width: "100%",
                height: "100%",
                maxWidth: "inherit",
                maxHeight: "inherit",
                margin: "0 auto",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                aspectRatio: !item.width || !item.height
                    ? AppImageDimension.CardPicture.width / AppImageDimension.CardPicture.height
                    : item.width > AppImageDimension.CardPicture.width || item.height > AppImageDimension.CardPicture.height || item.type == "custom-video-slide"
                        ? (item.width > item.height ? item.width / item.height : item.height / item.width)
                        : undefined
            }}>
            {
                item.type == "custom-video-slide" &&
                <MprcVideoBanner {...item} {...rest} />
            }
            {
                item.type == "image" &&
                <ImageBanner {...item} {...rest} />
            }
        </div>
    );
};

////////////////////////////////////////////////////////////////////////////////

interface ContentMediaProps {
    publicationId: string;
    presentation: PublicationPresentation | null;
    attachs: FeedAttachDto[] | null;
    cardOwnProps: CardOwnPropsOptions;
    onUpdateCardOwnProps: (data: Partial<CardOwnPropsOptions>) => void;
    videoEvents?: CustomVideoSlideEvents;
}

export const ContentMedia = () => {
    const { pub: { publicationId, presentation, attachs }, cardOwnProps, onUpdateCardOwnProps } = useCardContext();

    const location = useLocation();
    const params = location.state;
    const api = useCoreApi();

    const [search, setSearch] = useState<CardVideoSearch>();

    useEffect(() => {
        setSearch(params ? { attachId: params["attachId"], searchText: params["searchText"] } : undefined);
    }, [params]);

    ///
    const trackingProgress = (attachId: string, playTime: number, ended: boolean) => {
        if (playTime > 0) {
            var a = attachs?.find(p => p.attachId == attachId);
            if (a && !a.mediaCompleted) {
                const lp = a.lastMediaPosition ?? 0;
                const diffTime = Math.trunc(playTime - lp);

                // Grava a cada 10 segundos ou se terminou.
                if (diffTime > 10 || ended) {
                    a.lastMediaPosition = playTime;
                    api.v1.knowledgePublication.trackMedia({ publicationId, attachId, playTime })
                        .then((response) => {
                            if (response.data.success) a!.mediaCompleted = response.data.mediaCompleted;
                        });
                }
            }
        }
    };

    ///
    const onMediaPlay = (attachId: string, seconds: number) => {
        var a = attachs?.find(p => p.attachId == attachId);
        if (a && a.mediaCompleted == false && (a as any)["firstPlay"] == undefined) {
            (a as any)["firstPlay"] = false;
            return a.lastMediaPosition ?? 0;
        }
        return null;
    };

    return <InternalContentMedia
        publicationId={publicationId}
        presentation={presentation}
        attachs={attachs}
        cardOwnProps={cardOwnProps.search != search ? { ...cardOwnProps, search } : cardOwnProps}
        onUpdateCardOwnProps={onUpdateCardOwnProps}

        videoEvents={
            {
                onPlay: onMediaPlay,
                onProgress: (videoId, props) => trackingProgress(videoId, props.playedSeconds, false),
                onEnded: (videoId, playedSeconds) => trackingProgress(videoId, playedSeconds, true),
            }
        }
    />;
};

export const InternalContentMedia = (props: ContentMediaProps) => {
    const { publicationId, presentation, attachs, cardOwnProps, onUpdateCardOwnProps } = props;
    const [currentBannerIndex, setCurrentBannerIndex] = useState<number>(0);
    const [playing, setPlaying] = useState<boolean>(false);
    const [openLightbox, setOpenLightbox] = useState<number | null>(null);
    const divRef = useRef<HTMLDivElement | null>(null);

    ///
    const slides = useMemo(() => {
        const slides: Array<CustomVideoSlide | SlideImage> = [];
        if (presentation) {
            // Prepara Slides.
            for (let i = 0; i < presentation.orderedItems.length; i++) {
                var at = attachs?.find(p => p.attachId == presentation.orderedItems[i]);
                if (at) {
                    const slide = BuildLighboxSlide(at, false, props.videoEvents);
                    if (slide != null) slides.push(slide);
                }
            }
        }
        return slides;
    }, [publicationId]);

    ///
    const updateOwnProps = (index: number) => {
        onUpdateCardOwnProps({
            presentation: {
                ...cardOwnProps.presentation,
                currentAttachId: presentation?.orderedItems[index] ?? "",
                currentIsVideo: slides.length > 0 && slides[index].type == "custom-video-slide"
            }
        });
    };

    ///
    useEffect(() => {
        if (cardOwnProps.search) {
            var index = slides.findIndex(p => p.type == "custom-video-slide" && p.id == cardOwnProps.search?.attachId);
            if (index != -1 && index != currentBannerIndex) {
                setCurrentBannerIndex(index);
                updateOwnProps(index);
            }
        }
        else {
            updateOwnProps(0);
        }
    }, [cardOwnProps.search]);

    ///
    const RenderSearchBox = () => {
        const currentMedia = slides[currentBannerIndex];

        return cardOwnProps.search
            && currentMedia.type == "custom-video-slide"
            && currentMedia.id == cardOwnProps.search.attachId
            && <VideoSearchBox attachId={cardOwnProps.search.attachId} searchText={cardOwnProps.search.searchText} />;
    };

    ///
    return (!attachs || !presentation || presentation.orderedItems?.length == 0 || slides.length == 0)
        ? null
        : (
            <>
                {openLightbox != null &&
                    <CardLightBox
                        slides={slides}
                        open
                        index={openLightbox}
                        autoplay={false}
                        rotationInterval={0}
                        enterFullScreen={true}
                        onClose={() => {
                            setOpenLightbox(null);
                        }}
                    />
                }

                <div ref={divRef}>
                    <Carousel
                        index={currentBannerIndex}
                        className="carousel"
                        fullHeightHover={false}
                        autoPlay={!playing && cardOwnProps.presentation.autoplay && !cardOwnProps.presentation.lighboxOpened}
                        interval={presentation.rotationInterval * 1000}
                        indicators={slides.length > 1}
                        NextIcon={<FontAwesomeIcon icon={faCaretCircleRight} />}
                        PrevIcon={<FontAwesomeIcon icon={faCaretCircleLeft} />}
                        navButtonsWrapperProps={{ className: "feedCarouselButton" }}
                        stopAutoPlayOnHover
                        offsetTop={70} // 66 (Barra de Voltar) + 4 (Indicadores do Carrossel)
                        observeWindowHeight
                        onChange={
                            (now, previous) => {
                                setCurrentBannerIndex(now!);
                                updateOwnProps(now!);
                            }
                        }
                    >
                        {
                            slides.map((item, index) => {
                                return <Banner
                                    key={index}
                                    item={item}
                                    offset={index - currentBannerIndex}
                                    videoAutoPlay={cardOwnProps.videoAutoPlay ?? false}
                                    stopPlayback={cardOwnProps.presentation.lighboxOpened}
                                    onPlayback={(playing) => setPlaying(playing)}
                                    onClick={(() => setOpenLightbox(index))}
                                />;
                            })
                        }
                    </Carousel>
                    {RenderSearchBox()}
                </div>
            </>
        );
};
